import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DocumentScanner, VideoCameraBackOutlined } from '@mui/icons-material';
import { MediaFile } from 'types/files';
import useLocalization from 'hooks/useLocalization';

interface FilesGalleryViewProps {
  files: MediaFile[];
  type: string;
  handleDeleteFileFromGallery: (file: MediaFile) => void;
  onEditExistingNameClick: (file: MediaFile) => void;
  handleLoadFilesListOnScroll: (e: any, offset: number) => void;
  noActions?: boolean;
  /** Dense cards for picker dialogs */
  compact?: boolean;
  onClickAction: (file: any) => void;
}

const FilesGalleryView: React.FC<FilesGalleryViewProps> = ({
  files,
  type,
  handleDeleteFileFromGallery,
  onEditExistingNameClick,
  handleLoadFilesListOnScroll,
  noActions,
  compact = false,
  onClickAction,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLButtonElement) | null>(null);
  const [selectedFile, setSelectedFile] = useState<MediaFile | null>(null);
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(() => currentLocale.settingsPopup.uploadFiles, [currentLocale]);

  const easingStandard = theme.transitions.easing.easeInOut;
  const durationShort = theme.transitions.duration.short;
  const cardMotionTransition = theme.transitions.create(['transform'], {
    duration: durationShort,
    easing: easingStandard,
  });
  const overlayOpacityTransition = theme.transitions.create(['opacity'], {
    duration: durationShort,
    easing: easingStandard,
  });
  /** compact: hover feedback via transform + opacity only (no animated border/box-shadow on the card itself) */
  const compactCardTransformTransition = theme.transitions.create(['transform'], {
    duration: durationShort,
    easing: easingStandard,
  });
  const compactCardRingOpacityTransition = theme.transitions.create(['opacity'], {
    duration: theme.transitions.duration.shorter,
    easing: easingStandard,
  });
  const topAccentTransition = theme.transitions.create(['opacity'], {
    duration: durationShort,
    easing: easingStandard,
  });

  const handleContextMenu = (event: React.MouseEvent<HTMLButtonElement>, file: MediaFile) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      setSelectedFile(null);
    }, 300);
  };

  const gridSize = compact
    ? { xs: 6, sm: 4, md: 3, lg: 3, xl: 2 }
    : { xs: 12, sm: 6, md: 4, lg: 3, xl: 2.4 };
  const minCardWidth = compact ? 0 : 220;
  const aspect = compact ? 16 / 10 : 2 / 1.25;
  const iconSize = compact ? 36 : 60;

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        maxHeight: '100%',
        overflow: 'auto',
        px: compact ? 0.5 : 1,
        py: compact ? 0.5 : 1.5,
        boxSizing: 'border-box',
      }}
      onScroll={(e) => handleLoadFilesListOnScroll(e, 200)}
    >
      <Grid container spacing={compact ? 1.5 : 3} sx={{ padding: compact ? '4px' : '8px' }}>
        {files.map((file) => (
          <Grid
            key={file.Id}
            size={gridSize}
            sx={{
              minWidth: minCardWidth,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Card
              sx={{
                transition: compact ? compactCardTransformTransition : cardMotionTransition,
                cursor: 'pointer',
                borderRadius: compact ? 1.5 : 2,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                overflow: 'hidden',
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...(compact && {
                  isolation: 'isolate',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    opacity: 0,
                    transition: compactCardRingOpacityTransition,
                    pointerEvents: 'none',
                    zIndex: 1,
                  },
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.015)',
                    '& .file-overlay': { opacity: 1 },
                    ...(type === 'image'
                      ? {
                          '& .files-gallery-card-media': { transform: 'scale(1.03)' },
                        }
                      : {}),
                  },
                  '&:hover::after': {
                    opacity: 1,
                  },
                }),
                ...(!compact && {
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.01)',
                    boxShadow: theme.shadows[8],
                    borderColor: 'primary.main',
                    '& .file-overlay': {
                      opacity: 1,
                    },
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    insetInline: 0,
                    top: 0,
                    height: 3,
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                    opacity: 0,
                    transition: topAccentTransition,
                    pointerEvents: 'none',
                  },
                  '&:hover::before': {
                    opacity: 1,
                  },
                  '&:hover .files-gallery-card-media': {
                    transform: 'scale(1.03)',
                  },
                }),
              }}
              onClick={() => onClickAction(file)}
            >
              <Box sx={{ position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
                {type === 'image' ? (
                  <CardMedia
                    className="files-gallery-card-media"
                    component={'img'}
                    height="100%"
                    image={file.Link}
                    alt={file.Name}
                    loading="lazy"
                    sx={{
                      aspectRatio: aspect,
                      objectFit: 'cover',
                      transform: 'scale(1)',
                      transformOrigin: 'center',
                      transition: theme.transitions.create(['transform'], {
                        duration: durationShort,
                        easing: easingStandard,
                      }),
                    }}
                  />
                ) : (
                  <Box
                    height="100%"
                    sx={{
                      aspectRatio: aspect,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.2), transparent)',
                      },
                    }}
                  >
                    {type === 'video' && (
                      <VideoCameraBackOutlined
                        titleAccess="Video"
                        sx={{
                          fontSize: iconSize,
                          color: 'white',
                          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                          zIndex: 1,
                        }}
                      />
                    )}
                    {type === 'document' && (
                      <DocumentScanner
                        titleAccess="Document"
                        sx={{
                          fontSize: iconSize,
                          color: 'white',
                          filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                          zIndex: 1,
                        }}
                      />
                    )}
                  </Box>
                )}
                <Box
                  className="file-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.1) 100%)',
                    opacity: 0,
                    transition: overlayOpacityTransition,
                    pointerEvents: 'none',
                  }}
                />
              </Box>

              <CardContent
                sx={{
                  padding: compact ? '8px 10px 10px' : '16px 20px 20px',
                  backgroundColor: 'background.paper',
                  borderTop: 'none',
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  ...(!compact && type !== 'image'
                    ? {
                        background: 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(248,250,252,0.98))',
                        backdropFilter: 'blur(10px)',
                      }
                    : {}),
                }}
              >
                <Grid container spacing={compact ? 0.5 : 1} alignItems={'center'}>
                  <Grid size={12}>
                    <Tooltip title={file.Name} placement="bottom-start">
                      <Typography
                        variant={compact ? 'body2' : 'h6'}
                        component="div"
                        sx={{
                          fontSize: compact ? '0.8rem' : '1rem',
                          fontWeight: compact ? 500 : 600,
                          color: 'text.primary',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%',
                          lineHeight: 1.35,
                          mb: compact ? 0.25 : 0.5,
                        }}
                      >
                        {file.Name}
                      </Typography>
                    </Tooltip>
                  </Grid>
                  <Grid size="grow">
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontSize: compact ? '0.7rem' : '0.875rem',
                        fontWeight: 400,
                        display: 'block',
                      }}
                    >
                      {file.DateCreate}
                    </Typography>
                  </Grid>
                  {!noActions && (
                    <Grid size="auto">
                      <IconButton
                        onClick={(event) => handleContextMenu(event, file)}
                        aria-label="File actions"
                        sx={{
                          padding: '6px',
                          borderRadius: 1,
                          color: 'text.secondary',
                          transition: compact
                            ? theme.transitions.create(['transform'], {
                                duration: theme.transitions.duration.shorter,
                                easing: easingStandard,
                              })
                            : theme.transitions.create(['transform', 'background-color', 'color'], {
                                duration: theme.transitions.duration.shorter,
                                easing: easingStandard,
                              }),
                          '&:hover': compact
                            ? {
                                transform: 'scale(1.08)',
                                color: 'primary.main',
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              }
                            : {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                transform: 'scale(1.08)',
                              },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: '1.25rem' }} />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Menu open={Boolean(anchorEl)} onClose={handleClose} anchorEl={anchorEl}>
        <MenuItem
          onClick={() => {
            if (selectedFile) handleDeleteFileFromGallery(selectedFile);
            handleClose();
          }}
        >
          {localization.menu.delete}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedFile) onEditExistingNameClick(selectedFile);
            handleClose();
          }}
        >
          {localization.menu.editName}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FilesGalleryView;
